/**
 * Generic keyed cell-data store.
 *
 * Same shape as the existing `SourceRowCellData` (per-sheet,
 * `RowLocalId`-keyed) but parameterized over the key type so the
 * Client-scoped canonical store can use it too
 * (`CellDataStore<string>`, keyed by canonical lookup-key).
 *
 * Consumers access this class as `SourceRow.CellDataStore` via
 * declaration-merging in `variants/source-row.ts`. The class is
 * defined here in its own module to keep the variant file
 * focused on the base `ModelRow` shim; the namespace-merge in
 * `source-row.ts` re-binds it under `SourceRow.*` so call sites
 * read as `SourceRow.CellDataStore<K>`.
 *
 * Semantics mirror `SourceRowCellData`:
 *   - Per-key cardinality routing via an inner `RowCellData`
 *     (single → sorted date index; multi → cell-id map).
 *   - `set` / `clear` fire per-key `subscribe` handlers (no
 *     global invalidation sink — consumers translate to their
 *     own event vocabulary, e.g. `ColumnDataChanged`).
 *   - `init` populates without emitting (hydrate-time seeding).
 *
 * Why this generalization exists: canonical provider
 * rows are sheet-invariant, so one Client-scoped store holds
 * their cells; multiple `Sheet`s view the same cells by
 * subscribing to the canonical lookup-key.
 */

import type { Option } from "../core/option.ts";
import { type DateTime, NaiveDate, type Utc } from "@tento-chrono";
import { DependencyAspect } from "@tento-core/dependency-graph";
import { ListenerErrors, type ListenerErrorSink, type Thrown } from "@tento-core/listener-errors";

import type { Actor } from "../tx/invalidation.ts";
import type { CellEntry, CellStyle, CellValue } from "../core/dtype.ts";
import type { CellLedgerBump } from "./ledger-keys.ts";
// `../tx/tx-log` is a genuine LEAF (no address-of / variants value imports), so
// value-importing `SheetTxOp` (for its bounded `summarize`) does NOT reopen the
// source-row TDZ cycle. `SheetTxDetail` is type-only.
import { SheetTxOp, type SheetTxDetail } from "../tx/tx-log.ts";
import { RowCellData } from "./row-cell-data.ts";
import { sameCellStyle, sameCellValue } from "./same-cell-value.ts";
import type { Unsubscribe } from "../backends/rows-backend.ts";
import type { CellCardinality } from "../core/dtype.ts";
import { CellId } from "../ids/ids.ts";

/** Mutation event observed via `CellDataStore.subscribe`. `actor`
 *  carries the originator's identity when the write was tagged with one
 *  (see `SourceRowCellData.set(..., actor)`); `null` when the writer
 *  didn't supply an actor (sync replay, formula compute, untagged
 *  writes). Used by the lona-js bridge to mirror local writes into
 *  `RowCellCache._fetched` with origin preserved, so plugin
 *  `CellHandle.onData` callbacks surface `change.actor` for self-echo
 *  suppression. A set of a cell with an id names the entry it replaced
 *  (`previous`, `null` when the id was new), so a watcher can place a moved
 *  cell without having seen its earlier writes (`FormulaCoverage`). */
export type CellDataChange<K> =
  | {
    readonly kind: "set";
    readonly key: K;
    readonly entry: CellEntry;
    readonly actor: Option<Actor>;
    /** The entry this set replaced (a set with a `cellId` only; `null` otherwise or when new). */
    readonly previous: Option<CellEntry>;
  }
  | {
    readonly kind: "clear";
    readonly key: K;
    readonly date: NaiveDate.Partial;
    readonly cellId?: CellId;
    readonly actor: Option<Actor>;
  }
  | {
    readonly kind: "set-custom";
    readonly key: K;
    readonly entry: CellEntry.Timepoint;
    readonly actor: Option<Actor>;
    /** The timepoint cell this set replaced, `null` when the id was new. */
    readonly previous: Option<CellEntry.Timepoint>;
  }
  | {
    readonly kind: "clear-custom";
    readonly key: K;
    readonly cellId: CellId;
    readonly dtr: DateTime.Range<Utc>;
    readonly actor: Option<Actor>;
  };

export type CellDataChangeHandler<K> = (change: CellDataChange<K>) => void;
/**
 * Called as a server-snapshot install bracket (`asInstall`) opens. A returned
 * function runs once the bracket closes (thrown or not).
 */
export type StoreInstallObserver = () => void | (() => void);

/** Observer of `CellDataStore.batch` boundaries — see `subscribeBatch`. */
export interface CellDataBatchHandler {
  begin(): void;
  end(): void;
}

/**
 * Generic cell-data store keyed by `K`. Two intended scales:
 *
 *   - **Per-sheet** — `CellDataStore<RowLocalId>`, owned by
 *     `Sheet`. Holds computed / user-backed cells for that
 *     sheet. Peer of `SourceRowCellData` (which will eventually
 *     unify into this class).
 *   - **Per-client** — `CellDataStore<string>`, owned by the
 *     consumer (lona-js's `Client` or equivalent). Keyed by
 *     canonical lookup-key (e.g. `:~weather:us-sf@…`). Holds
 *     provider-backed cells that every subscribing sheet
 *     consumes.
 *
 * Agnostic to scale — the key type determines ownership.
 */
export class CellDataStore<K> {
  /** The fresh copies every row of this store made of written values (`ownsCellValue`). */
  readonly #copyLedger = new WeakSet<object>();
  readonly #sharedSubscriptions = new Map<object, { count: number; release: Unsubscribe }>();
  readonly #byKey = new Map<K, RowCellData>();
  readonly #cardinalityByKey = new Map<K, CellCardinality>();
  readonly #handlersByKey = new Map<
    K,
    Set<CellDataChangeHandler<K>>
  >();
  readonly #anyHandlers = new Set<CellDataChangeHandler<K>>();
  readonly #batchHandlers = new Set<CellDataBatchHandler>();
  #batchDepth = 0;
  /**
   * Per-key map of "stale" cell identities — cells written since the
   * last `markFresh`. `set` and `clear` auto-populate; `init`
   * (hydrate seeding) does not. The formula engine reads this via
   * `isStale` / `anyStaleInRange` to short-circuit
   * `recomputeDependents` when every upstream cell in the range has
   * already been consumed.
   *
   * Inner key shape: `<date-string>|<cellId?>`. The entry stores the
   * `NaiveDate.Partial` so range checks don't need to round-trip
   * through `cells(range)` — that's important for `clear`, which
   * removes the cell from `byKey` but leaves the stale bit.
   *
   * Consumers that don't care about staleness ignore the API —
   * everything else continues to work unchanged.
   */
  readonly #staleByKey = new Map<
    K,
    Map<string, NaiveDate.Partial>
  >();

  /**
   * Optional cap on the number of keys held simultaneously. When
   * set and the store exceeds it, the least-recently-accessed key
   * is evicted (cells + cardinality dropped). Sheet-scoped stores
   * leave this unset — row counts are bounded by the sheet itself.
   * Client-scoped stores (keyed by canonical lookup-key) set a cap
   * to keep weather-style fan-out from growing unbounded. Design
   * doc suggests ~500; defaults to `undefined` (unbounded) here so
   * tests and per-sheet instances aren't affected. Subscribers on
   * an evicted key remain attached; they'll re-fire when the key
   * repopulates.
   */
  readonly #capacity: Option<number>;
  readonly #maxCells: Option<number>;
  readonly #pins = new Map<K, number>();
  readonly #costs = new Map<K, number>();
  #cellsHeld = 0;
  readonly #guards = new Map<K, { allowed: () => boolean; evicted: () => void }>();
  /** A placement or admitted write holds facts until its release; releases are idempotent. */
  retain(key: K): Unsubscribe {
    this.#pins.set(key, (this.#pins.get(key) ?? 0) + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const count = this.#pins.get(key) ?? 0;
      if (count <= 1) this.#pins.delete(key); else this.#pins.set(key, count - 1);
      // An unpin can make only this key evictable: try it alone (a full pass
      // per release made n releases scan n keys each).
      if (count <= 1) this.#evictKeyIfOverCapacity(key);
    };
  }
  /**
   * SDK cache ownership participates in idle eviction without retaining sheet
   * closures. Returns the release: the owner going away (a client's dispose)
   * drops its guard, so a store that outlives it keeps no reference to the
   * owner's cache; a later guard on the key is left alone.
   */
  guard(key: K, allowed: () => boolean, evicted: () => void): Unsubscribe {
    const entry = { allowed, evicted };
    this.#guards.set(key, entry);
    // A guard coming or going changes only its own row's evictability: that
    // row becomes a deferred candidate (no full pass).
    this.#deferCandidate(key);
    return () => {
      if (this.#guards.get(key) === entry) {
        this.#guards.delete(key);
        this.#deferCandidate(key);
      }
    };
  }

  /**
   * LR1 (row-identity ledger, L2) — the shared `LedgerBumper` + a per-key
   * `data:` node mapper. Present when the store was hydrated through an
   * accessor that owns a ledger; `null` for standalone / test stores, where
   * every mutation method's bump is a no-op. Never an event — a bump is a
   * counter beside the existing emit path.
   */
  readonly #cellBump: Option<CellLedgerBump<K>>;
  /**
   * Local-write revisions (project sheet-scroll-smoothness, candidate D). A
   * server snapshot must not overwrite a cell written after the snapshot was
   * requested — a user edit, a live update, a clear (a deleted event). Every
   * write outside an `asInstall` bracket bumps `writeRev` and stamps the
   * cell's *storage* identity (a multi-cardinality or custom cell by its id, a
   * single-cardinality cell by its day — the same identity `RowCellData` keys
   * on); a fetch captures the revision when its request goes out and its
   * install skips any cell stamped later, whether the write landed during the
   * round trip or during the ingestion-gate wait. Installs run under
   * `asInstall`, so two snapshots never mask each other. The stamps are
   * bounded: `captureRevision` registers an outstanding snapshot and
   * `releaseRevision` prunes every stamp no outstanding snapshot can still
   * refer to — with nothing outstanding the map is empty, so the bookkeeping
   * costs nothing while the gate is open.
   */
  #writeRev = 0;
  #installing = 0;
  readonly #installObservers = new Set<StoreInstallObserver>();
  readonly #localWrites = new Map<K, Map<string, number>>();
  readonly #outstanding = new Map<number, number>();
  readonly #retentionCaptures = new Map<number, number>();
  readonly #admitted = new Set<K>();
  /** Whether a value or clear was already admitted, including an empty-row tombstone. */
  hasAdmitted(key: K): boolean { return this.#admitted.has(key) || this.has(key); }

  constructor();
  constructor(opts: CellDataStoreOptions<K>);
  constructor(opts: Option<CellDataStoreOptions<K>> = null) {
    this.#capacity = opts?.capacity ?? null;
    this.#maxCells = opts?.maxCells ?? null;
    this.#cellBump = opts?.cellBump ?? null;
    this.#listenerErrors = opts?.listenerErrors ?? ListenerErrors.RETHROW;
  }

  /** Where a subscriber's error goes (`CellDataStoreOptions.listenerErrors`): never into the writer. */
  readonly #listenerErrors: ListenerErrorSink;

  /** Where this store reports a throwing subscriber (`CellDataStoreOptions.listenerErrors`). */
  get listenerErrors(): ListenerErrorSink {
    return this.#listenerErrors;
  }

  /**
   * LR1: bump the `data:` VALUES version for `key` — O(1), synchronous, beside
   * (never through) the emit path. No-op without a ledger binding. `init`
   * (hydrate seed) and the staleness marks deliberately do NOT call this: they
   * are not data changes (`init` seeds pre-subscribe; marks are bookkeeping).
   *
   * TX0: routes through `bumpOp` so the bump also records `detail` (the write
   * point holds date / cellId / value) — one op record per bump. Inert until an
   * accessor attaches a recorder (`bumpOp` still just bumps otherwise).
   */
  /** Monotone count of local writes; the revision a fresh install compares against. */
  get writeRevision(): number {
    return this.#writeRev;
  }

  /** True inside an `asInstall` bracket — a mirror reads it to carry provenance. */
  get isInstalling(): boolean {
    return this.#installing > 0;
  }

  /** Register an outstanding snapshot at the current revision; pair with `releaseRevision`. */
  captureRevision(): number {
    const rev = this.#writeRev;
    this.#outstanding.set(rev, (this.#outstanding.get(rev) ?? 0) + 1);
    this.#retentionCaptures.set(rev, (this.#retentionCaptures.get(rev) ?? 0) + 1);
    return rev;
  }

  /** The snapshot captured at `rev` installed or bailed: prune what it alone protected. */
  releaseRevision(rev: number): void {
    const held = this.#retentionCaptures.get(rev);
    if (held == null) return;
    if (held > 1) this.#retentionCaptures.set(rev, held - 1);
    else this.#retentionCaptures.delete(rev);
    this.#releaseRevision(rev);
    // Captures block eviction outright; the release that ends the last one
    // is the one that can free rows, so it alone runs a full pass.
    if (this.#retentionCaptures.size === 0) this.#evictIfOverCapacity(null);
  }

  /** A long-lived seed snapshot protects its own keys without pinning unrelated idle rows. */
  holdRevision(keys: Iterable<K>): { readonly revision: number; readonly release: Unsubscribe } {
    const revision = this.#writeRev;
    this.#outstanding.set(revision, (this.#outstanding.get(revision) ?? 0) + 1);
    const pins: Unsubscribe[] = [];
    try {
      for (const key of keys) pins.push(this.retain(key));
    } catch (error) {
      // Admission owns the marker and every hold acquired before iteration
      // failed. Cleanup must not replace the original admission error.
      try { this.#releaseHeldRevision(revision, pins); }
      finally { throw error; }
    }
    let released = false;
    return { revision, release: () => {
      if (released) return;
      released = true;
      this.#releaseHeldRevision(revision, pins);
    } };
  }

  /**
   * Admit a read against exactly this resident and local-write clock.
   * Pin before materializing even an empty row; forget/recreate invalidates
   * identity independently of pins. The lease owns one marker and one hold.
   */
  captureRead(key: K): {
    readonly revision: number;
    readonly resident: RowCellData;
    readonly current: () => boolean;
    readonly release: Unsubscribe;
  } {
    const held = this.holdRevision([key]);
    let active = true;
    try {
      const resident = this.#ensureStore(key);
      return {
        revision: held.revision,
        resident,
        current: () => active && this.#byKey.get(key) === resident,
        release: () => {
          if (!active) return;
          active = false;
          held.release();
        },
      };
    } catch (error) {
      active = false;
      try { held.release(); } finally { throw error; }
    }
  }

  /** Attempt every owned release; observer failures retain first-error identity. */
  #releaseHeldRevision(revision: number, pins: readonly Unsubscribe[]): void {
    let failure: Option<() => never> = null;
    try { this.#releaseRevision(revision); }
    catch (error) { failure = () => { throw error; }; }
    for (const pin of pins) {
      try { pin(); }
      catch (error) {
        if (failure == null) failure = () => { throw error; };
      }
    }
    if (failure != null) failure();
  }

  #releaseRevision(rev: number): void {
    const n = this.#outstanding.get(rev);
    if (n == null) return;
    if (n > 1) {
      // The minimum outstanding revision did not move: nothing new to prune.
      this.#outstanding.set(rev, n - 1);
    } else {
      this.#outstanding.delete(rev);
      // A stamp at or below every outstanding revision protects nothing, so
      // keeping one until the minimum moves changes no read; prune only then.
      let min = Infinity;
      for (const r of this.#outstanding.keys()) if (r < min) min = r;
      if (rev < min) this.#pruneLocalWrites();
    }
    // The rows a write left over capacity go first, as a full pass would free them.
    this.#evictDeferred(null);
  }

  /** @internal Test hook: stamps currently retained. */
  localWriteCount(): number {
    let n = 0;
    for (const map of this.#localWrites.values()) n += map.size;
    return n;
  }

  /** Run a server-snapshot install: its writes are not local writes. */
  asInstall<T>(fn: () => T): T {
    const finishers = this.#openInstall();
    this.#installing++;
    let failure: Option<() => never> = null;
    try {
      return fn();
    } catch (error) {
      failure = () => { throw error; };
      throw error;
    } finally {
      this.#installing--;
      this.#finishInstall(finishers, failure);
    }
  }

  /**
   * Observe every `asInstall` bracket on this store (instrumentation seam: the
   * SDK's `ingestion.onStoreInstall`). Returns the unsubscribe.
   */
  observeInstalls(observer: StoreInstallObserver): Unsubscribe {
    const entry: StoreInstallObserver = () => observer();
    this.#installObservers.add(entry);
    return () => {
      this.#installObservers.delete(entry);
    };
  }

  #openInstall(): Array<() => void> {
    if (this.#installObservers.size === 0) return [];
    const finishers: Array<() => void> = [];
    try {
      for (const observer of [...this.#installObservers]) {
        const finish = observer();
        if (typeof finish === "function") finishers.push(finish);
      }
    } catch (error) {
      this.#finishInstall(finishers, () => { throw error; });
      throw error;
    }
    return finishers;
  }

  /** Finish in opening order, preserving a body/opener error before cleanup errors. */
  #finishInstall(finishers: readonly (() => void)[], primary: Option<() => never>): void {
    let failure = primary;
    for (const finish of finishers) {
      try { finish(); }
      catch (error) {
        if (failure == null) failure = () => { throw error; };
      }
    }
    if (failure != null) failure();
  }

  /**
   * True when `key`'s cell was written locally after `since`. Stamps are
   * written under the storage identity (id for multi, day for single); the
   * lookup checks both candidate identities, so it does not depend on the
   * cardinality still being known — an evicted key keeps its stamps but
   * not its cardinality.
   */
  writtenLocallySince(
    key: K,
    since: number,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): boolean {
    const map = this.#localWrites.get(key);
    if (map == null) return false;
    const byDay = map.get("date:" + date.toString());
    if (byDay != null && byDay > since) return true;
    if (cellId == null) return false;
    const byId = map.get("id:" + cellId);
    return byId != null && byId > since;
  }

  /** True when `key`'s custom (timepoint) cell `id` was written locally after `since`. */
  customWrittenLocallySince(key: K, since: number, id: string): boolean {
    const r = this.#localWrites.get(key)?.get("id:" + id);
    return r != null && r > since;
  }

  #identityOf(key: K, date: NaiveDate.Partial, cellId: Option<CellId>): string {
    return cellId != null && this.#cardinalityByKey.get(key) === "multi"
      ? "id:" + cellId
      : "date:" + date.toString();
  }

  #recordLocalWrite(key: K, date: NaiveDate.Partial, cellId: Option<CellId>): void {
    this.#stamp(key, this.#identityOf(key, date, cellId));
  }

  #recordLocalCustomWrite(key: K, id: string): void {
    this.#stamp(key, "id:" + id);
  }

  #stamp(key: K, identity: string): void {
    this.#admitted.add(key);
    if (this.#installing > 0) return;
    this.#writeRev++;
    if (this.#outstanding.size === 0) return; // nothing waiting: no stamp can matter
    let map = this.#localWrites.get(key);
    if (map == null) {
      map = new Map();
      this.#localWrites.set(key, map);
    }
    map.set(identity, this.#writeRev);
  }

  #pruneLocalWrites(): void {
    if (this.#outstanding.size === 0) {
      this.#localWrites.clear();
      return;
    }
    let min = Infinity;
    for (const r of this.#outstanding.keys()) if (r < min) min = r;
    for (const [key, map] of this.#localWrites) {
      for (const [identity, r] of map) if (r <= min) map.delete(identity);
      if (map.size === 0) this.#localWrites.delete(key);
    }
  }

  #bumpData(key: K, detail: SheetTxDetail): void {
    const cb = this.#cellBump;
    if (cb == null) return;
    cb.bumper.bumpOp(cb.bumpKeyFor(key), DependencyAspect.VALUES, "data", detail);
  }

  /**
   * Declare a key's cell cardinality. `single` uses the sorted
   * date index; `multi` uses a cell-id map. Immutable once a
   * store with data exists — re-declaring throws.
   */
  /** The actual declared shape, independent of whether a resident has cells. */
  declaredCardinality(key: K): Option<CellCardinality> {
    return this.#cardinalityByKey.get(key) ?? null;
  }

  setCardinality(key: K, cardinality: CellCardinality): void {
    const declared = this.#cardinalityByKey.get(key);
    if (declared != null && declared !== cardinality) {
      throw new Error(
        `CellDataStore.setCardinality: key already declares ${declared}; cannot change to ${cardinality}.`,
      );
    }
    const existing = this.#byKey.get(key);
    if (existing && existing.cardinality !== cardinality) {
      throw new Error(
        `CellDataStore.setCardinality: key already has a ` +
          `${existing.cardinality} store with data; cannot change to ${cardinality}.`,
      );
    }
    // Refusal must leave the storage identity and local-write guards intact.
    this.#cardinalityByKey.set(key, cardinality);
  }

  /** Read one cell. `null` if unset. */
  get(
    key: K,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): Option<CellValue> {
    const store = this.#byKey.get(key);
    if (!store) return null;
    this.#touch(key, store);
    return store.get(date, cellId);
  }

  /** Read every cell for `key`, optionally filtered to a range. */
  cells(key: K, range: Option<NaiveDate.Range>): readonly CellEntry[] {
    const store = this.#byKey.get(key);
    if (!store) return EMPTY;
    this.#touch(key, store);
    return store.cells(range);
  }

  /**
   * Write one cell. Fires `subscribe` handlers with
   * `{kind: "set", key, entry, actor}`. `style` carries the optional
   * per-cell style bag populated by formula `cell.bg` calls. `actor`
   * tags the originator so downstream consumers (e.g. the lona-js
   * bridge → `RowCellCache.applyCellWriteLocal`) can preserve origin for
   * self-echo suppression in plugin cells. Pass `null` for untagged
   * writes (sync replay, formula compute, internal seeding).
   *
   * Inside an `asInstall` bracket a cell the store already holds unchanged —
   * the same day, id, structurally equal value and equal style — is skipped
   * whole: no write, no stale mark, no event, no ledger bump. A server
   * re-delivery (overlapping fetch padding, a sync refetch, a window read by
   * both the batched and the per-row fetch) then costs no invalidation. A
   * local write (outside `asInstall`) always writes and emits, as before;
   * installs never stamp local writes, so `writtenLocallySince` is unchanged.
   */
  set(
    key: K,
    date: NaiveDate.Partial,
    value: CellValue,
    cellId: Option<CellId>,
    style: Option<CellStyle>,
    actor: Option<Actor>,
  ): void {
    const store = this.#ensureStore(key);
    // A multi-cardinality row without an id is refused by the write below.
    const held = store.cardinality !== "multi" || cellId != null ? store.peekEntry(date, cellId) : null;
    if (this.#installing > 0 && held != null && sameHeldCell(held, date, value, cellId, style)) return;
    const previous = cellId != null ? held : null;
    store.set(
      date,
      value,
      cellId,
      style,
    );
    this.#markStaleInternal(key, date, cellId);
    this.#recordLocalWrite(key, date, cellId);
    const entry: CellEntry = {
      date,
      id: cellId ?? null,
      value,
      style: style ?? null,
    };
    this.#emit(key, { kind: "set", key, entry, actor, previous });
    this.#bumpData(key, {
      t: "cell/set",
      date: date.toString(),
      cellId: cellId ?? null,
      value: SheetTxOp.summarize(value),
    });
  }

  /**
   * Seed one cell without emitting. Used by hydrate paths to
   * populate the store before subscribers attach.
   */
  init(
    key: K,
    date: NaiveDate.Partial,
    value: CellValue,
    cellId: Option<CellId>,
    style: Option<CellStyle>,
  ): void {
    this.#ensureStore(key).init(date, value, cellId, style);
    this.#rememberCost(key);
    this.#evictIfOverCapacity(key);
  }

  /**
   * Delete one cell. Fires `subscribe` handlers with
   * `{kind: "clear", key, date, cellId, actor}` — even if no cell was
   * present (matches `SourceRowCellData.clear`'s emit-always
   * semantics). `actor` tags the originator; pass `null` for untagged
   * clears.
   */
  clear(
    key: K,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    actor: Option<Actor>,
  ): void {
    const store = this.#maxCells == null ? this.#byKey.get(key) : this.#ensureStore(key);
    if (store) store.clear(date, cellId);
    this.#markStaleInternal(key, date, cellId);
    this.#recordLocalWrite(key, date, cellId);
    this.#emit(key, {
      kind: "clear",
      key,
      date,
      ...(cellId != null ? { cellId } : {}),
      actor,
    });
    this.#bumpData(key, {
      t: "cell/clear",
      date: date.toString(),
      cellId: cellId ?? null,
    });
  }

  // ── Custom (datetime-positioned) cells ──────────────────────────
  //
  // Parallel to the date-keyed `set`/`clear`/`cells` surface. Datetime
  // cells (sticker overlays, scheduling slots) live in the per-key
  // `RowCellData` custom lane, independent of cardinality. Emits
  // `set-custom`/`clear-custom` so the lona-js bridge mirrors them into
  // the `RowCellCache` custom store and subscribers re-render.

  /**
   * Upsert one datetime cell. Fires `subscribe` handlers with
   * `{kind: "set-custom", key, entry, actor}`. `actor` tags the
   * originator for self-echo suppression; pass `null` for untagged
   * writes (sync replay, server reconcile).
   */
  setCustom(
    key: K,
    entry: CellEntry.Timepoint,
    actor: Option<Actor>,
  ): void {
    const store = this.#ensureStore(key);
    const previous = store.getCustom(entry.id);
    store.setCustom(entry);
    this.#recordLocalCustomWrite(key, entry.id);
    this.#emit(key, { kind: "set-custom", key, entry, actor, previous });
    // Mark the timepoint's day stale so formula dependents recompute — the
    // semantic `set`/`clear` path does this, and timed events (now custom
    // cells) must trigger the same dependency cascade.
    this.#markStaleInternal(
      key,
      NaiveDate.Partial.fromDate(entry.dtr.start.date),
      entry.id,
    );
    this.#bumpData(key, {
      t: "cell/set-custom",
      cellId: entry.id,
      value: SheetTxOp.summarize(entry.value),
    });
  }

  /**
   * The count of `key`'s date-keyed and timepoint cells, read off its
   * per-row store without listing them. Counts as a read (the key moves to
   * the recent end), like `cells`.
   */
  cellCount(key: K): number {
    const store = this.#byKey.get(key);
    if (!store) return 0;
    this.#touch(key, store);
    return store.size;
  }

  /** Read datetime cells for `key`, optionally filtered to a UTC range. */
  customCells(
    key: K,
    range: Option<DateTime.Range<Utc>>,
  ): readonly CellEntry.Timepoint[] {
    const store = this.#byKey.get(key);
    if (!store) return EMPTY_CUSTOM;
    this.#touch(key, store);
    return store.customCells(range);
  }

  /** Read one datetime cell by raw id. `null` if unset. */
  getCustom(key: K, id: string): Option<CellEntry.Timepoint> {
    const store = this.#byKey.get(key);
    if (!store) return null;
    this.#touch(key, store);
    return store.getCustom(id);
  }

  /**
   * Delete one datetime cell by raw id. Fires `subscribe` handlers
   * with `{kind: "clear-custom", key, cellId, dtr, actor}` when a cell
   * was present (the range is needed so subscribers can invalidate the
   * right columns). No-op + no emit when absent.
   */
  removeCustom(key: K, id: string, actor: Option<Actor>): void {
    const store = this.#byKey.get(key);
    if (!store) return;
    const dtr = store.removeCustom(id);
    if (dtr == null) return;
    this.#recordLocalCustomWrite(key, id);
    this.#emit(key, { kind: "clear-custom", key, cellId: CellId.of(id), dtr, actor });
    // Stale-mark the day so formula dependents recompute after a removal,
    // mirroring `clear` on the semantic lane.
    this.#markStaleInternal(
      key,
      NaiveDate.Partial.fromDate(dtr.start.date),
      CellId.of(id),
    );
    this.#bumpData(key, { t: "cell/remove-custom", cellId: CellId.of(id) });
  }

  /**
   * Replace every datetime cell under `key` whose start falls in
   * `range` with `entries` — the reconcile primitive a server custom-
   * cell write uses to settle local optimistic state to server truth.
   * Emits one `set-custom` per surviving entry; the range clear is
   * silent (callers that need a clear signal pass it explicitly).
   */
  replaceCustomInRange(
    key: K,
    range: DateTime.Range<Utc>,
    entries: readonly CellEntry.Timepoint[],
    actor: Option<Actor>,
  ): void {
    const store = this.#ensureStore(key);
    for (const removed of this.customCells(key, range)) this.#recordLocalCustomWrite(key, removed.id);
    const previous = new Map(entries.map((entry) => [entry.id, store.getCustom(entry.id)]));
    store.clearCustomInRange(range);
    for (const entry of entries) {
      this.#recordLocalCustomWrite(key, entry.id);
      store.setCustom(entry);
      this.#emit(key, { kind: "set-custom", key, entry, actor, previous: previous.get(entry.id) ?? null });
      this.#markStaleInternal(
        key,
        NaiveDate.Partial.fromDate(entry.dtr.start.date),
        entry.id,
      );
    }
    // One bump per affected key — the range replace is a single data change on
    // this key (O(rows), not O(cells)); the silent range-clear is folded in.
    this.#bumpData(key, { t: "cell/replace-custom-range", count: entries.length });
  }

  /** Access the underlying per-key `RowCellData`. Low-level. */
  rowData(key: K): Option<RowCellData> {
    return this.#byKey.get(key) ?? null;
  }

  /** True when a key has been written to or cardinality declared. */
  has(key: K): boolean {
    return this.#byKey.has(key) || this.#cardinalityByKey.has(key);
  }

  /**
   * Drop a key's cells and cardinality state. Does NOT emit per-
   * cell clear events (they'd be unbounded); callers that need
   * to signal consumers must do so themselves.
   */
  forget(key: K): void {
    this.#deferred.delete(key);
    this.#localWrites.delete(key);
    this.#admitted.delete(key);
    this.#cellsHeld -= this.#costs.get(key) ?? 0;
    this.#costs.delete(key);
    this.#guards.delete(key);
    this.#byKey.delete(key);
    this.#cardinalityByKey.delete(key);
    this.#staleByKey.delete(key);
    // The data under `key` went away — still a VALUES change on that node. (The
    // ledger node itself is not retired here; LR8 GC is a later slice.)
    this.#bumpData(key, { t: "cell/forget" });
  }

  // ── Staleness ───────────────────────────────────────────────────

  /**
   * True when the cell at `(key, date, cellId?)` was written via
   * `set` / `clear` after the most recent `markFresh`. Hydrate-time
   * seeding (`init`) doesn't set the bit.
   */
  isStale(key: K, date: NaiveDate.Partial, cellId: Option<CellId>): boolean {
    const map = this.#staleByKey.get(key);
    if (!map) return false;
    return map.has(staleId(date, cellId));
  }

  /**
   * True when any cell under `key` is currently stale. Cheap
   * range-level short-circuit for consumers that would otherwise
   * iterate every cell in a range to test staleness.
   */
  hasAnyStale(key: K): boolean {
    const map = this.#staleByKey.get(key);
    return map !== undefined && map.size > 0;
  }

  /**
   * True when any stale cell under `key` falls inside `range`.
   * Survives `clear` (which removes the cell from the main store
   * but leaves the stale bit) — unlike iterating `cells(key,
   * range)`, this sees cleared-but-not-yet-consumed cells.
   */
  anyStaleInRange(key: K, range: NaiveDate.Range): boolean {
    const map = this.#staleByKey.get(key);
    if (!map || map.size === 0) return false;
    for (const date of map.values()) {
      if (dateInRange(date, range)) return true;
    }
    return false;
  }

  /**
   * Iterate stale entries under `key`. Used by consumers that need
   * to process every pending change (e.g. the formula engine's
   * post-pass `markFresh`).
   */
  *staleEntries(
    key: K,
  ): Iterable<{ date: NaiveDate.Partial; cellId?: CellId }> {
    const map = this.#staleByKey.get(key);
    if (!map) return;
    for (const [k, date] of map) {
      const idx = k.indexOf("|");
      yield idx >= 0
        ? { date, cellId: CellId.of(k.slice(idx + 1)) }
        : { date };
    }
  }

  /**
   * Mark a cell stale. `set` / `clear` call this internally; public
   * callers use it when they need to signal staleness without a
   * value change (e.g. upstream dependency changed its attributes).
   */
  markStale(key: K, date: NaiveDate.Partial, cellId: Option<CellId>): void {
    this.#markStaleInternal(key, date, cellId);
  }

  /**
   * Clear the stale bit for a specific cell. Consumers (e.g. the
   * formula engine after a successful recompute) call this to
   * signal "I've consumed this change, don't re-trigger work."
   */
  markFresh(key: K, date: NaiveDate.Partial, cellId: Option<CellId>): void {
    const map = this.#staleByKey.get(key);
    if (!map) return;
    map.delete(staleId(date, cellId));
    if (map.size === 0) this.#staleByKey.delete(key);
  }

  /**
   * Clear every stale bit under `key` whose date falls inside
   * `range`. Used by `recomputeDependents` after consuming every
   * pending change in a window — per-cell `markFresh` would need a
   * redundant walk.
   */
  markFreshInRange(key: K, range: NaiveDate.Range): void {
    const map = this.#staleByKey.get(key);
    if (!map) return;
    for (const [k, date] of [...map]) {
      if (dateInRange(date, range)) map.delete(k);
    }
    if (map.size === 0) this.#staleByKey.delete(key);
  }

  /** Clear every stale bit under `key`. */
  markAllFresh(key: K): void {
    this.#staleByKey.delete(key);
  }

  #markStaleInternal(
    key: K,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): void {
    let map = this.#staleByKey.get(key);
    if (!map) {
      map = new Map();
      this.#staleByKey.set(key, map);
    }
    map.set(staleId(date, cellId), date);
  }

  /**
   * Subscribe to mutations on a specific `key`. Returns an
   * unsubscribe function.
   */
  subscribe(key: K, handler: CellDataChangeHandler<K>): Unsubscribe {
    let set = this.#handlersByKey.get(key);
    if (!set) {
      set = new Set();
      this.#handlersByKey.set(key, set);
    }
    set.add(handler);
    return () => {
      const current = this.#handlersByKey.get(key);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) this.#handlersByKey.delete(key);
    };
  }

  /**
   * One `subscribe()` per `holder` for as long as any of its callers holds it:
   * the first call subscribes, each call returns a release, and the last
   * release ends the subscription. The store owns the count, so a holder
   * shared by several views of this store (an invalidation sink) hears each
   * change once.
   */
  /**
   * Whether `value` is this store's own copy of a written cell value: a plain
   * object `frozenCopy` made fresh when a cell was set or installed here (a
   * caller's object, a proxy, a class instance or an already-frozen value
   * handed in is not). A reader that needs proof a value came from this
   * store's decode (not a look-alike) asks this instead of a module registry.
   * It proves STORE provenance, not row provenance: the ledger is one per
   * store, so a copy made for any key of this store answers true (a reader
   * that needs the row asks the row's cells for it).
   */
  ownsCellValue(value: object): boolean {
    return this.#copyLedger.has(value);
  }

  sharedSubscription(holder: object, subscribe: () => Unsubscribe): Unsubscribe {
    let entry = this.#sharedSubscriptions.get(holder);
    if (entry == null) {
      entry = { count: 0, release: subscribe() };
      this.#sharedSubscriptions.set(holder, entry);
    }
    entry.count++;
    const held = entry;
    return () => {
      if (--held.count === 0) {
        held.release();
        this.#sharedSubscriptions.delete(holder);
      }
    };
  }

  /**
   * Subscribe to mutations on every key. Returns an unsubscribe
   * function. Useful for consumers that want to translate every
   * change into their own invalidation vocabulary.
   */
  subscribeAll(handler: CellDataChangeHandler<K>): Unsubscribe {
    this.#anyHandlers.add(handler);
    return () => {
      this.#anyHandlers.delete(handler);
    };
  }

  /**
   * Bracket a burst of writes. Every `set` / `setCustom` / `clear`
   * inside still emits its own change (the vocabulary is unchanged),
   * but `subscribeBatch` observers are told where the burst starts and
   * ends, so a mirror that pays per change — the lona-js store→handle
   * bridge rebuilds a row's indices and fires its listeners on EVERY
   * change — can pay once per burst instead. Re-entrant: only the
   * outermost bracket notifies. Every end handler runs; what they throw goes
   * to the store's listener-error sink (`ListenerErrors.report`), never to
   * the caller: `batch` throws only its operation's own error.
   * Begin handlers and individual mutation dispatch retain their existing behavior.
   */
  batch<T>(fn: () => T): T {
    if (this.#batchDepth++ === 0) {
      for (const h of this.#batchHandlers) h.begin();
    }
    try {
      return fn();
    } finally {
      this.#endBatch();
    }
  }

  /** Run every still-registered end handler (live Set removals honoured); what they throw goes to the sink. */
  #endBatch(): void {
    if (--this.#batchDepth !== 0) return;
    const handlers = this.#batchHandlers;
    ListenerErrors.report(
      this.#listenerErrors,
      ListenerErrors.fanOut([...handlers], (h) => handlers.has(h), (h) => h.end()),
      "CellDataStore.batch",
    );
  }

  /** Observe `batch` boundaries. Returns an unsubscribe. */
  subscribeBatch(handler: CellDataBatchHandler): Unsubscribe {
    this.#batchHandlers.add(handler);
    return () => {
      this.#batchHandlers.delete(handler);
    };
  }

  /** Count of keys currently holding cell data. */
  size(): number {
    return this.#byKey.size;
  }

  #ensureStore(key: K): RowCellData {
    let store = this.#byKey.get(key);
    if (!store) {
      const cardinality = this.#cardinalityByKey.get(key) ?? "single";
      store = new RowCellData(cardinality);
      store.useCopyLedger(this.#copyLedger);
      this.#byKey.set(key, store);
      this.#evictIfOverCapacity(key);
    } else {
      this.#touch(key, store);
    }
    return store;
  }

  /**
   * Move `key` to the MRU end of `byKey`'s iteration order so the
   * next eviction pass drops stale keys first. Does nothing when
   * capacity is unset.
   */
  #touch(key: K, store: RowCellData): void {
    if (this.#capacity == null && this.#maxCells == null) return;
    this.#byKey.delete(key);
    this.#byKey.set(key, store);
    // Deferred rows keep least-recently-used order too.
    if (this.#deferred.delete(key)) this.#deferred.add(key);
  }

  /**
   * When over capacity, evict keys from the LRU end (the first key
   * in `byKey`'s iteration order) until we're back at the limit.
   * The just-inserted `protect` key is never evicted even if it's
   * the only one over — otherwise the store would churn on each
   * insert when capacity is set to a low number.
   */
  /** Keys the eviction passes have visited (`evictionVisitCount`). */
  #evictionVisits = 0;
  /**
   * Writes that may skip their pass: set when a write's full pass visited
   * every key and still ended over capacity (every other key pinned or its
   * guard refusing), to the number of keys it visited. A write changes only
   * its own key, which its pass protects, so the next writes could free
   * nothing more — unless a guard's answer changes without the store
   * hearing of it (an open sheet releasing its rows). Skipping as many
   * writes as the pass visited keys keeps writes O(1) amortized and retries
   * such a guard within `size()` writes. Unpins and guards installed or
   * released (`#deferred`), and the release ending the last capture (its
   * own full pass), never wait on it.
   */
  #exhaustedSkips = 0;
  /**
   * Rows a write left resident over capacity that no pass has tried since:
   * the protected key of a write whose pass ended (or was skipped) over
   * capacity. Each is tried once (`#evictDeferred`, in write order) by the
   * next write, revision release or unpin while the store is over
   * capacity, so a release frees an idle row a write left behind without
   * a full pass. A full pass that visits every key clears it.
   */
  readonly #deferred = new Set<K>();

  /** @internal Test hook: keys the eviction passes have visited so far. */
  evictionVisitCount(): number {
    return this.#evictionVisits;
  }

  #overCapacity(): boolean {
    return this.#byKey.size > (this.#capacity ?? Infinity) || this.#cellsHeld > (this.#maxCells ?? Infinity);
  }

  /** Try each deferred row once (but `protect`), while over capacity; O(1) amortized per write that deferred one. */
  #evictDeferred(protect: Option<K>): void {
    if (this.#deferred.size === 0) return;
    if (this.#capacity == null && this.#maxCells == null) return;
    if (this.#retentionCaptures.size > 0) return;
    for (const key of this.#deferred) {
      if (!this.#overCapacity()) return;
      if (key === protect) continue;
      this.#deferred.delete(key);
      const store = this.#byKey.get(key);
      if (store == null) continue;
      this.#evictionVisits++;
      if (this.#pins.has(key)) continue;
      const guard = this.#guards.get(key);
      if (guard != null && !guard.allowed()) continue;
      this.#evict(key, store);
    }
  }

  /**
   * `key`'s last pin went: it is now a candidate (the newest deferred one).
   * Over capacity, the deferred rows are tried in order — rows a write left
   * behind first, then this one; under capacity it waits for the write that
   * goes over, so a skipped write pass never strands it.
   */
  #evictKeyIfOverCapacity(key: K): void {
    this.#deferCandidate(key);
    this.#evictDeferred(null);
  }

  /** `key` may have become evictable: queue it (newest last) for the next attempt over capacity. */
  #deferCandidate(key: K): void {
    if (this.#capacity == null && this.#maxCells == null) return;
    if (!this.#byKey.has(key)) return;
    this.#deferred.delete(key);
    this.#deferred.add(key);
  }

  #evict(key: K, store: RowCellData): void {
    const guard = this.#guards.get(key);
    this.#deferred.delete(key);
    this.#byKey.delete(key);
    this.#admitted.delete(key);
    this.#cardinalityByKey.delete(key);
    this.#staleByKey.delete(key);
    this.#localWrites.delete(key);
    this.#guards.delete(key);
    const cost = this.#costs.get(key) ?? store.size;
    this.#cellsHeld -= cost;
    this.#costs.delete(key);
    guard?.evicted();
  }

  #evictIfOverCapacity(protect: Option<K>): void {
    if (this.#capacity == null && this.#maxCells == null) return;
    // Global captures protect cells and clear tombstones until every parked response releases.
    if (this.#retentionCaptures.size > 0) return;
    let rows = this.#byKey.size;
    let cells = this.#cellsHeld;
    if (rows <= (this.#capacity ?? Infinity) && cells <= (this.#maxCells ?? Infinity)) return;
    if (protect != null && this.#exhaustedSkips > 0) {
      this.#exhaustedSkips--;
      this.#evictDeferred(protect);
      if (this.#overCapacity()) this.#deferred.add(protect);
      return;
    }
    let visited = 0;
    for (const [key, store] of this.#byKey) {
      if (rows <= (this.#capacity ?? Infinity) && cells <= (this.#maxCells ?? Infinity)) {
        this.#exhaustedSkips = 0;
        return;
      }
      this.#evictionVisits++;
      visited++;
      if (key === protect || this.#pins.has(key)) continue;
      const guard = this.#guards.get(key);
      if (guard != null && !guard.allowed()) continue;
      const cost = this.#costs.get(key) ?? store.size;
      this.#evict(key, store);
      rows--;
      cells -= cost;
    }
    // Visited every key and still over capacity: only `protect` may yet be freed by a release.
    const over = rows > (this.#capacity ?? Infinity) || cells > (this.#maxCells ?? Infinity);
    this.#exhaustedSkips = over ? visited : 0;
    this.#deferred.clear();
    if (over && protect != null) this.#deferred.add(protect);
  }

  #rememberCost(key: K): void {
    if (this.#maxCells == null) return;
    const cost = this.#byKey.get(key)?.size ?? 0;
    this.#cellsHeld += cost - (this.#costs.get(key) ?? 0);
    this.#costs.set(key, cost);
  }

  /**
   * Tell the key's and the store's subscribers, then evict over capacity.
   * Subscribers are isolated: each one runs whatever an earlier one threw,
   * the write and its eviction always complete, and what they threw goes to
   * the store's listener-error sink once (`ListenerErrors.report`). The
   * report runs even when the eviction throws (an eviction guard's
   * `evicted` callback), so a subscriber's error is never lost behind it.
   */
  #emit(key: K, change: CellDataChange<K>): void {
    this.#rememberCost(key);
    const errors: Thrown[] = [];
    const perKey = this.#handlersByKey.get(key);
    if (perKey) ListenerErrors.fanOut([...perKey], (h) => perKey.has(h), (h) => h(change), errors);
    const all = this.#anyHandlers;
    ListenerErrors.fanOut([...all], (h) => all.has(h), (h) => h(change), errors);
    try {
      this.#evictIfOverCapacity(key);
    } finally {
      ListenerErrors.report(this.#listenerErrors, errors, "CellDataStore");
    }
  }
}

/**
 * Construction options for `CellDataStore`. `capacity` bounds the number of
 * keys held (LRU eviction past it; unbounded when unset); `cellBump` binds
 * the LR1 ledger bump.
 */
export interface CellDataStoreOptions<K> {
  readonly capacity?: number;
  /** Where a subscriber's error goes; `ListenerErrors.RETHROW` (the host's uncaught error) when unset. */
  readonly listenerErrors?: ListenerErrorSink;
  /** Bound loaded idle cell data; retained placements, admitted operations and active cache readers are pinned. */
  readonly maxCells?: number;
  readonly cellBump?: CellLedgerBump<K>;
}

const EMPTY: readonly CellEntry[] = Object.freeze([]);
const EMPTY_CUSTOM: readonly CellEntry.Timepoint[] = Object.freeze([]);

/**
 * Whether `held` (the entry a store keeps) is the cell `set` is asked to
 * write: the same day (type and index), on a multi-cardinality row the same
 * id (a single row keys by day and keeps no id), the same value
 * (`sameCellValue`: structural for plain data) and the same style.
 */
function sameHeldCell(
  held: CellEntry,
  date: NaiveDate.Partial,
  value: CellValue,
  cellId: Option<CellId>,
  style: Option<CellStyle>,
): boolean {
  return held.date.type === date.type &&
    held.date.index === date.index &&
    (held.id == null || held.id === cellId) &&
    sameCellStyle(held.style, style ?? null) &&
    sameCellValue(held.value, value);
}

function staleId(date: NaiveDate.Partial, cellId: Option<CellId>): string {
  return cellId != null ? `${date.toString()}|${cellId}` : date.toString();
}

function dateInRange(
  date: NaiveDate.Partial,
  range: NaiveDate.Range,
): boolean {
  // Half-open `[start, end)` on either endpoint of the partial date.
  return (
    (date.start.dse >= range.start.dse && date.start.dse < range.end.dse) ||
    (date.end.dse > range.start.dse && date.end.dse <= range.end.dse)
  );
}
